import { BACKGROUND_EFFECTS, type BackgroundEffect } from './background-effect.ts'
import { DESKTOP_STEP_IDS, PHONE_STEP_IDS } from './effect-ladder.ts'
import {
  CONNECTION_TYPES,
  type ConnectionType,
  EFFECTIVE_TYPES,
  INVOKER_TYPES,
  type PageHealthTotals,
  type PageSnapshot,
  type PageStallReport,
  RECORDER_STATES,
  UI_PANELS,
} from './page-health.ts'
import { SEGMENTER_MODEL_IDS } from './segmenter-catalog.ts'
import { CONNECTION_QUALITIES, type ConnectionQualityName } from './teleconsultation.ts'

export type RoomEventScalar = string | number | boolean | null

export const ROOM_EVENT_TYPES = [
  'room.effect_measured',
  'room.effect_changed',
  'room.effect_summary',
  'room.effect_failed',
  'room.prejoin',
  'room.connected',
  'room.admission',
  'room.quality',
  'room.stream_state',
  'room.page_stall',
  'room.camera_off',
  'room.camera_on',
  'room.reconnecting',
  'room.reconnected',
  'room.floating_window',
  'room.recorder',
  'room.left',
  'room.dropped',
] as const

export type RoomEventType = (typeof ROOM_EVENT_TYPES)[number]

export type DroppableRoomEventType = Exclude<RoomEventType, 'room.dropped'>

export const DROPPABLE_ROOM_EVENT_TYPES: readonly DroppableRoomEventType[] =
  ROOM_EVENT_TYPES.filter((type): type is DroppableRoomEventType => type !== 'room.dropped')

const SUMMARY_STEP_IDS = [...PHONE_STEP_IDS, ...DESKTOP_STEP_IDS, 'floor'] as const

type SummaryStepId = (typeof SUMMARY_STEP_IDS)[number]

const ROOM_ROLES = ['owner', 'guest'] as const
export type RoomRole = (typeof ROOM_ROLES)[number]
const MEASURE_STAGES = ['prejoin', 'call'] as const
export type MeasureStage = (typeof MEASURE_STAGES)[number]
const ATTEMPT_RESULTS = [
  'passed',
  'slow',
  'wrong_output',
  'context_lost',
  'timeout',
  'cancelled',
] as const
export type AttemptResult = (typeof ATTEMPT_RESULTS)[number]
const CHANGE_REASONS = ['delivered', 'cost', 'gpu', 'wrong_output'] as const
export type ChangeReason = (typeof CHANGE_REASONS)[number]
const FAILURE_REASONS = [
  'unavailable',
  'init_failed',
  'attach_failed',
  'context_lost',
  'device_too_slow',
] as const
export type FailureReason = (typeof FAILURE_REASONS)[number]

const COMMON_KEYS = [
  'appointment_id',
  'role',
  'room_session',
  'revision',
  'device',
  'browser',
  'os',
] as const

const ROOM_DEVICES = ['mobile', 'desktop'] as const
export type RoomDevice = (typeof ROOM_DEVICES)[number]
export type RemoteRole = RoomRole | 'unknown'
const QUALITY_SCOPES = ['local_send', 'remote'] as const
export type QualityScope = (typeof QUALITY_SCOPES)[number]
export type QualityName = ConnectionQualityName | 'unknown'
export const LIMITATION_REASONS = ['none', 'cpu', 'bandwidth', 'other'] as const
export type LimitationReason = (typeof LIMITATION_REASONS)[number]
const ADMISSION_STATES = ['waiting', 'admitted'] as const
export type AdmissionState = (typeof ADMISSION_STATES)[number]
const STREAM_STATES = ['paused', 'active'] as const
export type StreamState = (typeof STREAM_STATES)[number]
const CAMERA_OFF_REASONS = ['user', 'effect_failed', 'strained', 'device_too_slow'] as const
export type RoomCameraOffReason = (typeof CAMERA_OFF_REASONS)[number]
const FLOATING_MODES = ['none', 'document', 'element'] as const
export type FloatingMode = (typeof FLOATING_MODES)[number]
const FLOATING_STATES = ['open', 'closed'] as const
export type FloatingState = (typeof FLOATING_STATES)[number]
const RECORDER_EVENTS = ['started', 'interrupted', 'resumed', 'stopped', 'finalized'] as const
export type RecorderEvent = (typeof RECORDER_EVENTS)[number]
const LEAVE_REASONS = ['left', 'removed', 'ended_by_owner', 'pagehide'] as const
export type LeaveReason = (typeof LEAVE_REASONS)[number]

export const ATTEMPT_FIELDS = [
  'step',
  'model',
  'delegate',
  'interval',
  'cost_p50',
  'cost_p90',
  'gpu_behind',
  'fps',
  'camera_fps',
  'long_frames',
  'iou',
  'result',
] as const

export type AttemptField = (typeof ATTEMPT_FIELDS)[number]
type AttemptKey = `attempt${1 | 2}_${AttemptField}`

const ATTEMPT_KEYS: readonly AttemptKey[] = ([1, 2] as const).flatMap((n) =>
  ATTEMPT_FIELDS.map((field): AttemptKey => `attempt${n}_${field}`),
)

const MEASURED_KEYS = [
  'platform',
  'gpu',
  'processor_path',
  'orientation',
  'start',
  'capture',
  'stage',
  'chosen_step',
  'duration_ms',
  'attempts',
] as const

const PAGE_SNAPSHOT_KEYS = [
  'page_blocking_ms',
  'page_layout_ms',
  'page_script_ms',
  'page_top_invoker_type',
  'page_top_source',
  'page_hidden_ms',
  'page_focused',
  'page_resize_loops',
  'page_battery_charging',
  'page_ui_panel',
  'page_recorder_state',
] as const

const TOTALS_KEYS = [
  'stalls',
  'loaf_count',
  'loaf_blocking_ms',
  'hidden_s',
  'resize_loops',
  'focused_share',
] as const

const CHANGED_KEYS = [
  'from_step',
  'to_step',
  'reason',
  'minute',
  'reasons',
  'window_delivered_fps',
  'window_camera_fps',
  'window_cost_p50',
  'window_hidden_ms',
  'window_long_ms',
] as const

const SUMMARY_KEYS = [
  'final_step',
  'step_downs',
  'cost_p50_first',
  'cost_p50_last',
  'gpu_behind_first',
  'gpu_behind_last',
  'fps_first',
  'fps_last',
  'camera_size',
  'sent_size',
  'quality_limitation',
  'context_lost',
] as const

type SecondsKey = `seconds_${SummaryStepId}`

const SUMMARY_SECONDS_KEYS: readonly SecondsKey[] = SUMMARY_STEP_IDS.map(
  (id): SecondsKey => `seconds_${id}`,
)

const FAILED_KEYS = ['stage', 'reason', 'step', 'capture', 'device_memory', 'minute'] as const

const PREJOIN_KEYS = ['effect', 'camera', 'mic', 'transcribe'] as const

const CONNECTED_KEYS = [
  'connect_ms',
  'reconnect',
  'cores',
  'device_memory',
  'screen_w',
  'screen_h',
  'dpr',
  'window_w',
  'window_h',
  'battery_charging',
  'connection_type',
  'save_data',
] as const

const QUALITY_KEYS = ['scope', 'quality', 'limitation_reason', 'loss_pct', 'rtt_ms'] as const

const STALL_KEYS = [
  'duration_s',
  'loaf_count',
  'loaf_blocking_ms',
  'loaf_max_ms',
  'loaf_layout_ms',
  'loaf_script_ms',
  'loaf_top_invoker_type',
  'loaf_top_source',
  'loaf_top_function',
  'hidden_ms',
  'focused',
  'resize_loops',
  'resize_events',
  'battery_charging',
  'battery_level',
  'connection_type',
  'connection_effective',
  'ui_panel',
  'ui_floating',
  'ui_fullscreen',
  'recorder_state',
  'remote_video_tracks',
] as const

const RECONNECT_KEYS = ['attempt', 'elapsed_ms', 'banner_shown'] as const

type RoomEventKey =
  | (typeof COMMON_KEYS)[number]
  | AttemptKey
  | (typeof MEASURED_KEYS)[number]
  | (typeof PAGE_SNAPSHOT_KEYS)[number]
  | (typeof TOTALS_KEYS)[number]
  | (typeof CHANGED_KEYS)[number]
  | (typeof SUMMARY_KEYS)[number]
  | SecondsKey
  | (typeof FAILED_KEYS)[number]
  | (typeof PREJOIN_KEYS)[number]
  | (typeof CONNECTED_KEYS)[number]
  | (typeof QUALITY_KEYS)[number]
  | (typeof STALL_KEYS)[number]
  | (typeof RECONNECT_KEYS)[number]
  | 'dropped'
  | 'key'
  | 'state'
  | 'remote_role'
  | 'raw_override'
  | 'mode'
  | 'segment'
  | 'duration_ms'

const CATALOG = {
  'room.effect_measured': [...COMMON_KEYS, ...MEASURED_KEYS, ...ATTEMPT_KEYS],
  'room.effect_changed': [...COMMON_KEYS, ...CHANGED_KEYS, ...PAGE_SNAPSHOT_KEYS],
  'room.effect_summary': [...COMMON_KEYS, ...SUMMARY_KEYS, ...SUMMARY_SECONDS_KEYS, ...TOTALS_KEYS],
  'room.effect_failed': [...COMMON_KEYS, ...FAILED_KEYS, ...PAGE_SNAPSHOT_KEYS],
  'room.prejoin': [...COMMON_KEYS, ...PREJOIN_KEYS],
  'room.connected': [...COMMON_KEYS, ...CONNECTED_KEYS],
  'room.admission': [...COMMON_KEYS, 'state'],
  'room.quality': [...COMMON_KEYS, ...QUALITY_KEYS],
  'room.stream_state': [...COMMON_KEYS, 'remote_role', 'state'],
  'room.page_stall': [...COMMON_KEYS, ...STALL_KEYS],
  'room.camera_off': [...COMMON_KEYS, 'reason', ...PAGE_SNAPSHOT_KEYS],
  'room.camera_on': [...COMMON_KEYS, 'raw_override'],
  'room.reconnecting': [...COMMON_KEYS, ...RECONNECT_KEYS],
  'room.reconnected': [...COMMON_KEYS, ...RECONNECT_KEYS],
  'room.floating_window': [...COMMON_KEYS, 'mode', 'state'],
  'room.recorder': [...COMMON_KEYS, 'state', 'segment'],
  'room.left': [...COMMON_KEYS, 'reason', 'duration_ms', ...TOTALS_KEYS],
  'room.dropped': [...COMMON_KEYS, 'dropped', 'key'],
} satisfies Readonly<Record<RoomEventType, readonly RoomEventKey[]>>

export const ROOM_EVENT_CATALOG: Readonly<Record<RoomEventType, readonly string[]>> = CATALOG

export const ROOM_EVENT_KEYS: readonly string[] = [...new Set(Object.values(CATALOG).flat())]

export const ROOM_EVENT_ENRICHMENT: Readonly<Record<RoomEventType, 'snapshot' | 'totals' | null>> =
  {
    'room.effect_measured': null,
    'room.effect_changed': 'snapshot',
    'room.effect_summary': 'totals',
    'room.effect_failed': 'snapshot',
    'room.prejoin': null,
    'room.connected': null,
    'room.admission': null,
    'room.quality': null,
    'room.stream_state': null,
    'room.page_stall': null,
    'room.camera_off': 'snapshot',
    'room.camera_on': null,
    'room.reconnecting': null,
    'room.reconnected': null,
    'room.floating_window': null,
    'room.recorder': null,
    'room.left': 'totals',
    'room.dropped': null,
  }

export const ROOM_TERMINAL_EVENT_TYPES: ReadonlySet<RoomEventType> = new Set(
  ROOM_EVENT_TYPES.filter((type) => ROOM_EVENT_ENRICHMENT[type] === 'totals'),
)

export interface RoomEventCommon {
  appointment_id: number
  role: RoomRole
  room_session: string
  revision?: string | null
  device?: RoomDevice | null
  browser?: string | null
  os?: string | null
}

type Optional<T> = { [K in keyof T]?: T[K] | null }

type AttemptValue<K extends AttemptKey> = K extends `${string}_result`
  ? AttemptResult
  : K extends `${string}_${'step' | 'model' | 'delegate'}`
    ? string
    : number

export type RoomEffectMeasured = RoomEventCommon &
  Optional<{
    platform: string
    gpu: string
    processor_path: 'generator' | 'canvas'
    orientation: string
    start: 'remembered' | 'default'
    capture: string
    stage: MeasureStage
    chosen_step: string
    duration_ms: number
    attempts: number
  }> &
  Optional<{ [K in AttemptKey]: AttemptValue<K> }>

export type RoomEffectChanged = RoomEventCommon & {
  from_step: string
  to_step: string
  reason: ChangeReason
  minute: number
} & Optional<{
    reasons: string
    window_delivered_fps: number
    window_camera_fps: number
    window_cost_p50: number
    window_hidden_ms: number
    window_long_ms: number
  }> &
  Optional<PageSnapshot>

export type RoomEffectSummary = RoomEventCommon &
  Optional<{
    final_step: string
    step_downs: number
    cost_p50_first: number
    cost_p50_last: number
    gpu_behind_first: number
    gpu_behind_last: number
    fps_first: number
    fps_last: number
    camera_size: string
    sent_size: string
    quality_limitation: string
    context_lost: boolean
  }> &
  Optional<{ [K in SecondsKey]: number }> &
  Optional<PageHealthTotals>

export type RoomEffectFailed = RoomEventCommon &
  Optional<{
    stage: MeasureStage
    reason: FailureReason
    step: string
    capture: string
    device_memory: number
    minute: number
  }> &
  Optional<PageSnapshot>

export type RoomPrejoin = RoomEventCommon &
  Optional<{ effect: BackgroundEffect; camera: boolean; mic: boolean; transcribe: boolean }>

export type RoomConnected = RoomEventCommon &
  Optional<{
    connect_ms: number
    reconnect: boolean
    cores: number
    device_memory: number
    screen_w: number
    screen_h: number
    dpr: number
    window_w: number
    window_h: number
    battery_charging: boolean
    connection_type: ConnectionType
    save_data: boolean
  }>

export type RoomAdmission = RoomEventCommon & { state: AdmissionState }

export type RoomQuality = RoomEventCommon & {
  scope: QualityScope
  quality: QualityName
} & Optional<{
    limitation_reason: LimitationReason
    loss_pct: number
    rtt_ms: number
  }>

export type RoomStreamState = RoomEventCommon & { remote_role: RemoteRole; state: StreamState }

export type RoomPageStall = RoomEventCommon & Optional<PageStallReport>

export type RoomCameraOff = RoomEventCommon & {
  reason: RoomCameraOffReason
} & Optional<PageSnapshot>

export type RoomCameraOn = RoomEventCommon & { raw_override: boolean }

export type RoomReconnect = RoomEventCommon &
  Optional<{ attempt: number; elapsed_ms: number; banner_shown: boolean }>

export type RoomFloatingWindow = RoomEventCommon & { mode: FloatingMode; state: FloatingState }

export type RoomRecorder = RoomEventCommon & { state: RecorderEvent } & Optional<{
    segment: number
  }>

export type RoomLeft = RoomEventCommon & { reason: LeaveReason } & Optional<{
    duration_ms: number
  }> &
  Optional<PageHealthTotals>

export type RoomDroppedKey = RoomEventKey | 'unknown'

export type RoomDropped = RoomEventCommon & {
  dropped: DroppableRoomEventType
  key: RoomDroppedKey
}

export interface RoomEventPayloads {
  'room.effect_measured': RoomEffectMeasured
  'room.effect_changed': RoomEffectChanged
  'room.effect_summary': RoomEffectSummary
  'room.effect_failed': RoomEffectFailed
  'room.prejoin': RoomPrejoin
  'room.connected': RoomConnected
  'room.admission': RoomAdmission
  'room.quality': RoomQuality
  'room.stream_state': RoomStreamState
  'room.page_stall': RoomPageStall
  'room.camera_off': RoomCameraOff
  'room.camera_on': RoomCameraOn
  'room.reconnecting': RoomReconnect
  'room.reconnected': RoomReconnect
  'room.floating_window': RoomFloatingWindow
  'room.recorder': RoomRecorder
  'room.left': RoomLeft
  'room.dropped': RoomDropped
}

export type ValueRule = 'int' | 'number' | 'bool' | readonly string[] | RegExp

const SDK_KEYS = ['event_type', 'ts', 'request_id'] as const

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/
const REVISION = /^(?:[0-9a-f]{7,40}(?:_uncommitted_[0-9a-f]{1,16})?)?$/
const BROWSER = /^(?:[A-Za-z]{2,16} \d{1,4}|other)$/
const GPU = /^(?:Apple|Adreno|Mali|Intel|NVIDIA|AMD)[A-Za-z0-9 -]{0,48}$/
const SIZE = /^\d{1,5}x\d{1,5}$/
const REASON_LIST = /^[a-z_]+(?:,[a-z_]+){0,5}$/
const SCRIPT_SOURCE = /^(?:inline|extension|other|[\w.-]{1,80}\.js)$/
const FUNCTION_NAME = /^[A-Za-z0-9_$.]{1,64}$/

const OS_LABELS = ['Android', 'iOS', 'iPadOS', 'macOS', 'Windows', 'ChromeOS', 'Linux', 'other']
const STEP_RULE: readonly string[] = SUMMARY_STEP_IDS
const REASON_RULE: readonly string[] = [
  ...new Set([...CHANGE_REASONS, ...FAILURE_REASONS, ...CAMERA_OFF_REASONS, ...LEAVE_REASONS]),
]
const STATE_RULE: readonly string[] = [
  ...new Set([...ADMISSION_STATES, ...STREAM_STATES, ...FLOATING_STATES, ...RECORDER_EVENTS]),
]

function ruled<K extends string>(
  keys: readonly K[],
  rule: (key: K) => ValueRule,
): Record<K, ValueRule> {
  const rules = {} as Record<K, ValueRule>
  for (const key of keys) rules[key] = rule(key)
  return rules
}

const ATTEMPT_FIELD_RULES: Readonly<Record<AttemptField, ValueRule>> = {
  step: STEP_RULE,
  model: SEGMENTER_MODEL_IDS,
  delegate: ['GPU', 'CPU'],
  interval: 'number',
  cost_p50: 'number',
  cost_p90: 'number',
  gpu_behind: 'number',
  fps: 'number',
  camera_fps: 'number',
  long_frames: 'int',
  iou: 'number',
  result: ATTEMPT_RESULTS,
}

function attemptRule(key: AttemptKey): ValueRule {
  const name = key.replace(/^attempt\d_/, '')
  const field = ATTEMPT_FIELDS.find((candidate) => candidate === name)
  return field ? ATTEMPT_FIELD_RULES[field] : []
}

const FIXED_VALUE_RULES: Readonly<
  Record<Exclude<RoomEventKey, AttemptKey | SecondsKey>, ValueRule>
> = {
  appointment_id: 'int',
  role: ROOM_ROLES,
  room_session: UUID,
  revision: REVISION,
  device: ROOM_DEVICES,
  browser: BROWSER,
  os: OS_LABELS,

  platform: ['phone', 'desktop'],
  gpu: GPU,
  processor_path: ['generator', 'canvas'],
  orientation: ['portrait', 'landscape'],
  start: ['remembered', 'default'],
  capture: ['normal', 'light'],
  stage: MEASURE_STAGES,
  chosen_step: STEP_RULE,
  duration_ms: 'number',
  attempts: 'int',

  page_blocking_ms: 'number',
  page_layout_ms: 'number',
  page_script_ms: 'number',
  page_top_invoker_type: INVOKER_TYPES,
  page_top_source: SCRIPT_SOURCE,
  page_hidden_ms: 'number',
  page_focused: 'bool',
  page_resize_loops: 'int',
  page_battery_charging: 'bool',
  page_ui_panel: UI_PANELS,
  page_recorder_state: RECORDER_STATES,

  stalls: 'int',
  loaf_count: 'int',
  loaf_blocking_ms: 'number',
  hidden_s: 'number',
  resize_loops: 'int',
  focused_share: 'number',

  from_step: STEP_RULE,
  to_step: STEP_RULE,
  reason: REASON_RULE,
  minute: 'int',
  reasons: REASON_LIST,
  window_delivered_fps: 'number',
  window_camera_fps: 'number',
  window_cost_p50: 'number',
  window_hidden_ms: 'number',
  window_long_ms: 'number',

  final_step: STEP_RULE,
  step_downs: 'int',
  cost_p50_first: 'number',
  cost_p50_last: 'number',
  gpu_behind_first: 'number',
  gpu_behind_last: 'number',
  fps_first: 'number',
  fps_last: 'number',
  camera_size: SIZE,
  sent_size: SIZE,
  quality_limitation: LIMITATION_REASONS,
  context_lost: 'bool',

  step: STEP_RULE,
  device_memory: 'number',

  effect: BACKGROUND_EFFECTS,
  camera: 'bool',
  mic: 'bool',
  transcribe: 'bool',

  connect_ms: 'number',
  reconnect: 'bool',
  cores: 'int',
  screen_w: 'number',
  screen_h: 'number',
  dpr: 'number',
  window_w: 'number',
  window_h: 'number',
  battery_charging: 'bool',
  connection_type: CONNECTION_TYPES,
  save_data: 'bool',

  scope: QUALITY_SCOPES,
  quality: [...CONNECTION_QUALITIES, 'unknown'],
  limitation_reason: LIMITATION_REASONS,
  loss_pct: 'number',
  rtt_ms: 'number',

  duration_s: 'number',
  loaf_max_ms: 'number',
  loaf_layout_ms: 'number',
  loaf_script_ms: 'number',
  loaf_top_invoker_type: INVOKER_TYPES,
  loaf_top_source: SCRIPT_SOURCE,
  loaf_top_function: FUNCTION_NAME,
  hidden_ms: 'number',
  focused: 'bool',
  resize_events: 'int',
  battery_level: 'number',
  connection_effective: EFFECTIVE_TYPES,
  ui_panel: UI_PANELS,
  ui_floating: 'bool',
  ui_fullscreen: 'bool',
  recorder_state: RECORDER_STATES,
  remote_video_tracks: 'int',

  attempt: 'int',
  elapsed_ms: 'number',
  banner_shown: 'bool',

  state: STATE_RULE,
  remote_role: [...ROOM_ROLES, 'unknown'],
  raw_override: 'bool',
  mode: FLOATING_MODES,
  segment: 'int',
  dropped: DROPPABLE_ROOM_EVENT_TYPES,
  key: [...ROOM_EVENT_KEYS, 'unknown'],
}

export const ROOM_EVENT_VALUE_RULES: Readonly<Record<RoomEventKey, ValueRule>> = {
  ...FIXED_VALUE_RULES,
  ...ruled(ATTEMPT_KEYS, attemptRule),
  ...ruled(SUMMARY_SECONDS_KEYS, () => 'number'),
}

const SDK_VALUE_RULES: Readonly<Record<(typeof SDK_KEYS)[number], ValueRule>> = {
  event_type: ROOM_EVENT_TYPES,
  ts: ISO_TIMESTAMP,
  request_id: UUID,
}

function matches(rule: ValueRule, value: string | number | boolean): boolean {
  if (rule === 'int') return typeof value === 'number' && Number.isInteger(value)
  if (rule === 'number') return typeof value === 'number' && Number.isFinite(value)
  if (rule === 'bool') return typeof value === 'boolean'
  if (typeof value !== 'string') return false
  return rule instanceof RegExp ? rule.test(value) : rule.includes(value)
}

function ruleOf(key: string): ValueRule | undefined {
  if (Object.hasOwn(SDK_VALUE_RULES, key))
    return SDK_VALUE_RULES[key as keyof typeof SDK_VALUE_RULES]
  if (Object.hasOwn(ROOM_EVENT_VALUE_RULES, key)) return ROOM_EVENT_VALUE_RULES[key as RoomEventKey]
  return undefined
}

function isRoomEventType(value: unknown): value is RoomEventType {
  return typeof value === 'string' && (ROOM_EVENT_TYPES as readonly string[]).includes(value)
}

function cleanScalar(value: unknown): RoomEventScalar | undefined {
  if (value === null || typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') return value
  return undefined
}

export interface RoomEventFailure {
  key: string
  rule: 'unknown_type' | 'unknown_key' | 'not_scalar' | 'value'
}

type Inspection =
  | { kept: Record<string, RoomEventScalar>; failure: null }
  | { kept: null; failure: RoomEventFailure }

function failed(key: string, rule: RoomEventFailure['rule']): Inspection {
  return { kept: null, failure: { key: ROOM_EVENT_KEYS.includes(key) ? key : 'unknown', rule } }
}

// unknown: the SDK hands the handler an untyped payload, and this function is what types it.
function inspect(payload: Readonly<Record<string, unknown>>): Inspection {
  const type = payload.event_type
  if (!isRoomEventType(type)) return failed('unknown', 'unknown_type')
  const allowed = new Set<string>([...SDK_KEYS, ...ROOM_EVENT_CATALOG[type]])
  const kept: Record<string, RoomEventScalar> = {}
  for (const key of Object.keys(payload)) {
    if (!allowed.has(key)) continue
    const raw = payload[key]
    if (raw === undefined) continue
    const value = cleanScalar(raw)
    if (value === undefined) return failed(key, 'not_scalar')
    if (value !== null) {
      const rule = ruleOf(key)
      if (!rule) return failed('unknown', 'unknown_key')
      if (!matches(rule, value)) return failed(key, 'value')
    }
    kept[key] = value
  }
  return { kept, failure: null }
}

export function filterRoomEvent(
  payload: Readonly<Record<string, unknown>>,
): Record<string, RoomEventScalar> | null {
  return inspect(payload).kept
}

export function explainRoomEvent(
  payload: Readonly<Record<string, unknown>>,
): RoomEventFailure | null {
  return inspect(payload).failure
}

export function normalizeGpu(renderer: string | null | undefined): string | null {
  if (!renderer) return null
  const apple = renderer.match(/Apple M\d+(?: (?:Pro|Max|Ultra))?/)
  if (apple) return apple[0]
  if (/Apple GPU/.test(renderer)) return 'Apple GPU'
  const adreno = renderer.match(/Adreno(?: \(TM\))? (\d+)/)
  if (adreno) return `Adreno ${adreno[1]}`
  const mali = renderer.match(/Mali-[A-Z]?\d+(?: MC\d+)?/)
  if (mali) return mali[0]
  if (/Intel/i.test(renderer)) {
    const intel = renderer
      .replace(/\((?:R|TM)\)/g, '')
      .match(/Iris Xe|Iris Plus|Iris Pro|UHD Graphics(?: \d+)?|HD Graphics(?: \d+)?/)
    if (intel) return `Intel ${intel[0]}`
  }
  const nvidia = renderer.match(/GeForce (?:GTX |RTX )?\d+\w*(?: Ti| SUPER)?/)
  if (nvidia) return `NVIDIA ${nvidia[0]}`
  const radeon = renderer.match(/Radeon (?:RX |Pro )?\w*\d+\w*(?: XT)?/)
  if (radeon) return `AMD ${radeon[0]}`
  return null
}
